//
// # SrvModel
//
// Owns the entire TS-side SRV story: the single state slot, the operations that
// mutate it, and the (nullable) logger registration. The workspace constructs
// one `SrvModel` at startup and exposes it as `workspace.srv`; callers go to
// `workspace.srv.runSrv()`, `workspace.srv.focusViolation(index)`, etc. This
// keeps `workspace.ts` free of a sprawl of SRV fields and methods, and keeps the
// `WorkspaceSnapshot` shape unchanged.
//
// Every dependency is injected (see `SrvModelDeps`) so the model is pure logic
// with no React, canvas, or HTTP coupling — and is unit-testable in isolation.
// The state is eagerly initialized to a default, so `getState()` is always
// non-null and has no precondition; a schematic-editing session that never
// touches SRV simply sees `lastRun === null` and the canvas overlay early-returns.
//

import { Bbox, Ident, srv } from "GenAlphaModel";
import { Context, Result } from "CoreUtils";
import { ActiveDiagramObserver } from "./activeDiagram";

export type SrvRuleDescriptor = srv.SrvRuleDescriptor;
export type SrvViolation = srv.SrvViolation;
export type SrvRunResult = srv.SrvRunResult;

// The read-only state slot, shared with the panel (React) and the canvas
// overlay (pushed across the EditorCore boundary by the workspace). Every
// violation is an error, so there is no severity field anywhere in SRV.
export interface SrvPanelState {
  rules: Array<SrvRuleDescriptor> | null; // read-only reference list; null until openPanel() loads it
  lastRun: SrvRunResult | null; // null if never run or results were cleared
  isRunning: boolean; // true while a run is in flight
  // True once the design has been edited since `lastRun` was produced: the
  // results are a snapshot, and an edit can move, remove, or fix the entities
  // they point at.
  //
  // This is advisory only. The list stays live and the arrows stay on the
  // canvas, because the point of the results is to work through them — fix one
  // violation, jump to the next — and forcing a re-run between every fix would
  // make that unusable. One fix rarely disturbs the other entries, so the rest
  // of the run stays useful. What the flag drives is the banner: it tells the
  // user the results describe the pre-edit design, so anything they have
  // already touched may be out of date, without taking the tool away.
  resultsStale: boolean;
  highlightedViolationIndex: number | null; // selected/hovered violation, for click-to-focus sync
  panelVisible: boolean; // true when the SrvPanel tab is the shown tab
  highlightsEnabled: boolean; // user "Hide Canvas Highlights" toggle; resets to true each Run
  schematicActive: boolean; // true when the active diagram is a schematic (gates the Run button)
}

// The logging surface the model forwards events to. Implemented in feature 5's
// `srvLogger.ts`; kept as an interface here so the model stays logger-agnostic
// and there is no import cycle. When no logger is registered the calls are
// silent no-ops (there is nothing meaningful to log without a mounted panel).
export interface SrvLogger {
  logRunInvocation(run: SrvRunResult, elapsedMs: number, runId: string): void;
  logRunFailed(why: string, runId: string): void;
  recordPanelClick(violationIndex: number, runId: string): void;
  recordCanvasClick(violationIndex: number, runId: string): void;
  setPanelVisible(visible: boolean): void;
  logResultsCleared(hadResults: boolean, violationCountAtClear: number): void;
}

export interface SrvModelDeps {
  // Fetch the read-only rule descriptor list from the Rust engine (via
  // AlphaServer). Async because it round-trips to the backend; injected so the
  // model is decoupled from its implementation and mockable.
  listSrvRules: (ctx: Context) => Promise<Result<Array<SrvRuleDescriptor>>>;
  // Fire a run-srv request for the active schematic. The rules run on the Rust
  // tool server; the result arrives asynchronously via `applySrvResult` (pushed
  // over the WebSocket). The returned Result reports only request-level
  // failures (no active schematic, transport error) so the model can clear the
  // running state and toast.
  requestSrv: (ctx: Context) => Promise<Result<void, { why: string }>>;
  // Persist the active schematic. "Check & Save" (formerly SRV) both validates
  // and saves, so a run first flushes pending edits and writes the design, then
  // runs the checks. A save failure is surfaced as a toast but does not block
  // the checks from running (save is a shortcut, not a gate).
  save: () => Promise<Result<unknown>>;
  // A fresh request context per operation; its logId becomes the run's runId so
  // a run correlates with its later click/focus logs (and with gcloud).
  makeContext: () => Context;
  // A cheap predicate for "is the active diagram a schematic" — used to gate the
  // Run button and the run entry point.
  isSchematicActive: () => boolean;
  // Reveal a violation's anchor bbox for focusViolation row clicks: pan the
  // canvas (without changing zoom) to center the bbox in the viewport.
  revealBbox: (bbox: Bbox) => void;
  // Transient user-facing message (run failures).
  showToast: (msg: string) => void;
  // Notify the host that state changed: the workspace re-pushes the snapshot to
  // the canvas overlay and triggers a React re-render.
  onChange: () => void;
  // Register for active-diagram-change notifications (observer pattern). The
  // model subscribes itself so stale results are cleared when the user switches
  // diagrams; the returned unsubscribe is unused (the model lives for the
  // whole session).
  watchActiveDiagram: (observer: ActiveDiagramObserver) => () => void;
}

export class SrvModel implements ActiveDiagramObserver {
  // Eagerly initialized so the state is never null and getState() has no
  // precondition. `schematicActive` is derived (computed live in getState), so
  // it is not stored here.
  private state: Omit<SrvPanelState, "schematicActive"> = {
    rules: null,
    lastRun: null,
    isRunning: false,
    resultsStale: false,
    highlightedViolationIndex: null,
    panelVisible: false,
    highlightsEnabled: true,
  };

  // The runId (Context logId) of the current lastRun, for correlating logs.
  private lastRunId: string | null = null;

  // The runId of an in-flight run, captured at request time so the async result
  // (applySrvResult/applySrvError) can correlate back to the originating run.
  private pendingRunId: string | null = null;

  private logger: SrvLogger | null = null;

  // Guards against concurrent rule-list fetches: ensureRulesLoaded fires from
  // the synchronous setPanelVisible, so rapid show/hide could otherwise launch
  // several in-flight requests.
  private rulesInFlight = false;

  constructor(private readonly deps: SrvModelDeps) {
    // Subscribe to active-diagram changes so results are cleared on a switch.
    this.deps.watchActiveDiagram(this);
  }

  // ActiveDiagramObserver: when the user switches to a different diagram, drop
  // any results so stale violations from the previous schematic aren't shown
  // against the new one. We notify on every switch — even an idle one — so the
  // panel re-renders the Run button's enabled/disabled state (which getState
  // derives live from `isSchematicActive`).
  onActiveDiagramChanged(_activeIdent: Ident | null): void {
    // clearResults already fires onChange; otherwise notify ourselves.
    if (this.state.lastRun !== null) {
      this.clearResults();
    } else {
      this.deps.onChange();
    }
  }

  // Mark the results stale after an edit. Called for every committed change
  // (see the Platform's notifyUIChange), so it is deliberately cheap and
  // idempotent: no-op when there is nothing to invalidate or it is already
  // marked, so a burst of edits fires at most one re-render.
  //
  // Marking is all this does. The highlight, the arrows, and the rows are all
  // left working — see `resultsStale` for why.
  markResultsStale(): void {
    if (this.state.lastRun === null || this.state.resultsStale) return;
    this.state.resultsStale = true;
    this.deps.onChange();
  }

  // Overlay gate: the same predicate the canvas module checks before drawing.
  // Exposed for non-canvas consumers (panel summary, logging). Staleness does
  // not gate this: the arrows stay up after an edit so the user can keep
  // working through the list.
  isActive(): boolean {
    return (
      this.state.lastRun !== null &&
      this.state.panelVisible &&
      this.state.highlightsEnabled
    );
  }

  // Read-only snapshot for the panel and the canvas overlay; always non-null.
  // Mutations go through the methods below, never through this object.
  // `schematicActive` is derived live (not stored) so it always reflects the
  // current active diagram — including the moment a freshly-opened schematic's
  // content finishes loading, which arrives via updateSnapshot, not a diagram
  // switch event.
  getState(): SrvPanelState {
    return { ...this.state, schematicActive: this.deps.isSchematicActive() };
  }

  registerLogger(logger: SrvLogger | null): void {
    this.logger = logger;
  }

  // Mark the panel visible (loading the rule list if needed). Retained as the
  // command entry point; the actual rule load now lives in setPanelVisible so
  // it happens however the panel is shown, not only via this command.
  openPanel(): void {
    this.setPanelVisible(true);
  }

  // Fire a run against the active schematic. The rules run on the Rust tool
  // server: AlphaServer serializes the current schematic and its referenced
  // symbols, sends them to Rust, and pushes the result back over the WebSocket,
  // where `applySrvResult` stores it. This method only kicks off the request
  // and reflects the in-flight (`isRunning`) state; request-level failures clear
  // it via `applySrvError`.
  async runSrv(): Promise<void> {
    if (!this.deps.isSchematicActive()) {
      this.deps.showToast("Check & Save: open a schematic first.");
      return;
    }

    const ctx = this.deps.makeContext();
    this.pendingRunId = ctx.logId ?? "";

    this.state.isRunning = true;
    this.deps.onChange();

    // Check & Save: persist the schematic before running the checks, so the
    // checks validate the just-saved state and the user's work is never lost
    // when they hit the shortcut. Saving also flushes any in-flight edits to the
    // server, which is the same state the checks read. A save failure is
    // surfaced but does not abort the checks — save is a convenience, not a gate.
    const saveRes = await this.deps.save();
    if (!saveRes.ok) {
      this.deps.showToast("Check & Save: save failed; running checks anyway.");
    }

    const res = await this.deps.requestSrv(ctx);
    if (!res.ok) {
      this.applySrvError(errorWhy(res.val));
    }
    // On success, do nothing here: the result arrives asynchronously via
    // `applySrvResult` (WebSocket push), which clears `isRunning`.
  }

  // Apply a successful SRV result pushed from the server. This is the async
  // counterpart to `runSrv`: it stores `lastRun`, resets the highlight toggle to
  // "show", clears the running state, and logs the run invocation.
  applySrvResult(result: SrvRunResult): void {
    const runId = this.pendingRunId ?? this.lastRunId ?? "";
    this.pendingRunId = null;

    this.state.lastRun = result;
    this.lastRunId = runId;
    this.state.isRunning = false;
    this.state.resultsStale = false;
    this.state.highlightsEnabled = true;
    this.state.highlightedViolationIndex = null;

    const elapsedMs = result.stats.reduce((sum, s) => sum + s.elapsedMs, 0);
    this.logger?.logRunInvocation(result, elapsedMs, runId);
    this.deps.onChange();
  }

  // Clear the in-flight state after a failed/aborted run and surface a toast.
  applySrvError(why: string): void {
    const runId = this.pendingRunId ?? "";
    this.pendingRunId = null;

    this.state.isRunning = false;
    this.logger?.logRunFailed(why, runId);
    this.deps.showToast(`Check & Save failed: ${why}`);
    this.deps.onChange();
  }

  setHighlightsEnabled(enabled: boolean): void {
    this.state.highlightsEnabled = enabled;
    this.deps.onChange();
  }

  // Clear results and remove canvas highlights.
  clearResults(): void {
    const hadResults = this.state.lastRun !== null;
    const count = this.state.lastRun?.violations.length ?? 0;
    this.logger?.logResultsCleared(hadResults, count);

    this.state.lastRun = null;
    this.state.resultsStale = false;
    this.state.highlightedViolationIndex = null;
    this.lastRunId = null;
    this.deps.onChange();
  }

  // Select a violation without panning/zooming — for arrow clicks on the canvas
  // (the user is already looking at the right spot).
  selectViolation(index: number): void {
    if (!this.isValidIndex(index)) return;
    this.state.highlightedViolationIndex = index;
    this.logger?.recordCanvasClick(index, this.lastRunId ?? "");
    this.deps.onChange();
  }

  // Select a violation for row clicks in the Results panel and pan (without
  // zooming) to center its arrow in the viewport. Stale results still focus:
  // the points describe the pre-edit design, which for everything the user has
  // not yet touched is still exactly where the violation is.
  focusViolation(index: number): void {
    if (!this.isValidIndex(index)) return;
    this.state.highlightedViolationIndex = index;
    this.logger?.recordPanelClick(index, this.lastRunId ?? "");

    const violation = this.state.lastRun?.violations[index];
    if (violation && violation.points.length > 0) {
      this.deps.revealBbox(Bbox.fromPoints(...violation.points));
    }
    this.deps.onChange();
  }

  setPanelVisible(visible: boolean): void {
    this.state.panelVisible = visible;
    // Load the read-only rule list the first time the panel is shown — by any
    // path, including a Dockview layout restored on reload, which never routes
    // through the openPanel command.
    if (visible) this.ensureRulesLoaded();
    this.logger?.setPanelVisible(visible);
    this.deps.onChange();
  }

  // Lazily fetch the rule descriptor list once, from the backend. On failure we
  // leave `rules` null; the panel shows its spinner/empty state and a later show
  // retries. The in-flight guard prevents duplicate fetches from rapid
  // show/hide. No throw, no fake data.
  private ensureRulesLoaded(): void {
    if (this.state.rules !== null || this.rulesInFlight) return;
    this.rulesInFlight = true;
    void this.deps.listSrvRules(this.deps.makeContext()).then((res) => {
      this.rulesInFlight = false;
      if (res.ok) {
        this.state.rules = res.val;
        // Notify so the panel re-renders now that the list has arrived.
        this.deps.onChange();
      }
    });
  }

  private isValidIndex(index: number): boolean {
    const run = this.state.lastRun;
    return run !== null && index >= 0 && index < run.violations.length;
  }
}

// Extract a short reason string from a Result error value, which is by
// convention a `{ why }` object but may be an arbitrary thrown value.
function errorWhy(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "object" && err !== null && "why" in err) {
    // `"why" in err` narrows `err` so `err.why` is accessible as `unknown`.
    const why: unknown = err.why;
    if (typeof why === "string") return why;
  }
  return "unknown-error";
}
